import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, ShieldCheck, Camera, Crop, Loader2, Lock, Upload, FileText, Globe, Copy, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { proShareUrl, copyToClipboard } from "@/lib/share";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceAreaPicker } from "@/components/ServiceAreaPicker";
import { PortraitFramingDialog } from "@/components/featured/PortraitFramingDialog";
import { toast } from "sonner";

const ORG_TYPES = [
  { value: "INDEPENDENT", label: "Independent Practitioner" },
  { value: "BROKERAGE", label: "Realty Brokerage" },
  { value: "DEVELOPER", label: "Property Developer" },
  { value: "PROPERTY_MGMT", label: "Property Management Company" },
  { value: "OTHER_ORG", label: "Other Organization" },
];
const ORG_NONE = "__none__";

// Professional Identity & Affiliation Phase 1 — grouped Sales/Business Position taxonomy.
const SALES_POSITION_GROUPS = [
  { group: "Executive / Division Leadership", items: [
    ["VP_SALES", "VP for Sales"], ["SALES_DIVISION_HEAD", "Sales Division Head"],
    ["SENIOR_SALES_DIRECTOR", "Senior Sales Director"], ["SALES_DIRECTOR", "Sales Director"],
    ["PROJECT_DIRECTOR", "Project Director"]] },
  { group: "Sales Leadership", items: [
    ["SENIOR_SALES_MANAGER", "Senior Sales Manager"], ["SALES_MANAGER", "Sales Manager"],
    ["ASSISTANT_SALES_MANAGER", "Assistant Sales Manager"], ["TEAM_LEADER", "Team Leader"],
    ["PROJECT_MANAGER", "Project Manager"]] },
  { group: "Sales / Advisory", items: [
    ["PROPERTY_CONSULTANT", "Property Consultant"], ["PROPERTY_SPECIALIST", "Property Specialist"],
    ["PROPERTY_ADVISOR", "Property Advisor"], ["SALES_EXECUTIVE", "Sales Executive"],
    ["ACCOUNT_EXECUTIVE", "Account Executive"]] },
  { group: "Brokerage / Leasing / Property Management", items: [
    ["BROKER", "Broker"], ["ASSOCIATE_BROKER", "Associate Broker"],
    ["LEASING_MANAGER", "Leasing Manager"], ["LEASING_CONSULTANT", "Leasing Consultant"],
    ["PROPERTY_MANAGER", "Property Manager"]] },
  { group: "Other Business Roles", items: [
    ["MARKETING_MANAGER", "Marketing Manager"], ["BUSINESS_DEV_MANAGER", "Business Development Manager"],
    ["DEVELOPER_REPRESENTATIVE", "Developer Representative"], ["OWNER_REPRESENTATIVE", "Owner Representative"],
    ["INDEPENDENT_PROFESSIONAL", "Independent Professional"], ["OTHER", "Other"]] },
];
const SALES_POS_NONE = "__none__";
const AFFILIATED_ORG_TYPES = [
  { value: "DEVELOPER", label: "Property Developer" },
  { value: "BROKERAGE", label: "Realty Brokerage" },
  { value: "PROPERTY_MGMT", label: "Property Management Company" },
  { value: "OTHER_ORG", label: "Other Organization" },
];

const VERIF_STYLES = {
  UNVERIFIED: "bg-attention-soft text-attention",
  IDENTITY_VERIFIED: "bg-info-soft text-info",
  PROFESSIONAL_VERIFICATION_PENDING: "bg-info-soft text-info",
  PROFESSIONAL_VERIFIED: "bg-success-soft text-success",
  VERIFICATION_REJECTED: "bg-critical-soft text-critical",
  SUSPENDED: "bg-critical-soft text-critical",
};
const label = (s) => (s || "UNVERIFIED").replace(/_/g, " ");

function PublicProfileManager({ account }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/public-profile").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s || (account && (account.account_type || "PROFESSIONAL").toUpperCase() === "CONSUMER")) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/me/public-profile", { public_profile_enabled: !s.public_profile_enabled });
      setS(data);
      toast.success(data.public_profile_enabled ? "Public profile published" : "Public profile unpublished");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const copy = async () => { const ok = await copyToClipboard(proShareUrl(s.public_slug)); toast(ok ? "Link copied" : "Copy failed"); };
  return (
    <section data-testid="public-profile-manager" className="bg-card border border-border rounded-xl p-6 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Globe className="h-5 w-5 text-accent" /> Public Profile</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-md">
            {s.public_profile_enabled
              ? "Your public professional profile is live and shareable."
              : "Publish a public professional profile you can share with clients and your network."}
          </p>
          {s.public_profile_enabled && s.public_slug && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">/pro/{s.public_slug}</code>
              <Button size="sm" variant="outline" onClick={copy} className="rounded-lg h-8" data-testid="copy-public-link"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
              <Link to={`/pro/${s.public_slug}`} target="_blank"><Button size="sm" variant="outline" className="rounded-lg h-8" data-testid="view-public-profile"><ExternalLink className="h-3.5 w-3.5 mr-1" /> View public profile</Button></Link>
              <Link to="/my-card"><Button size="sm" variant="outline" className="rounded-lg h-8" data-testid="go-my-card"><Globe className="h-3.5 w-3.5 mr-1" /> My Digital Card</Button></Link>
            </div>
          )}
          {!s.public_profile_enabled && (
            <div className="mt-3"><Link to="/my-card"><Button size="sm" variant="outline" className="rounded-lg h-8" data-testid="go-my-card-preview"><Globe className="h-3.5 w-3.5 mr-1" /> Preview My Digital Card</Button></Link></div>
          )}
        </div>
        <Button onClick={toggle} disabled={busy} data-testid="toggle-public-profile"
          className={s.public_profile_enabled ? "rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200" : "rounded-xl bg-accent text-accent-foreground hover:bg-accent/90"}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (s.public_profile_enabled ? "Unpublish" : "Publish & Preview")}
        </Button>
      </div>
    </section>
  );
}


export default function Profile() {
  const { refreshUser } = useAuth();
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [ver, setVer] = useState({ prc_number: "", role: "", broker_affiliation: "", document_note: "" });
  const fileRef = useRef();
  const docRef = useRef();

  const [framingOpen, setFramingOpen] = useState(false);
  const load = () => api.get("/me/profile").then((r) => { setP(r.data); setF(r.data); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/me/profile", {
        first_name: f.first_name, last_name: f.last_name, mobile: f.mobile, bio: f.bio,
        agency: f.agency, city: f.city, specializations: f.specializations,
        organization_type: f.organization_type || "",
        organization_name: f.organization_name || "",
        sales_position: f.sales_position || "",
        sales_position_other: f.sales_position_other || "",
        affiliation_status: f.affiliation_status || "",
        organization_branch: f.organization_branch || "",
        service_area_ids: (f.service_area_ids || []).map((a) => ({ code: a.code, level: a.level, name: a.name })),
      });
      toast.success("Profile updated"); setEdit(false); load(); refreshUser();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadAvatar = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/me/avatar", fd); toast.success("Photo updated"); load(); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changePw = async () => {
    try { await api.post("/me/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitVer = async () => {
    try { await api.post("/me/verification", ver); toast.success("Verification submitted for review"); load(); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadDoc = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/me/verification/document", fd); toast.success("Document uploaded (private)"); load(); refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!p) return <PageShell testid="profile-page"><p className="text-slate-500">Loading…</p></PageShell>;

  return (
    <PageShell testid="profile-page">
      <Fade>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-8">
          <div className="relative">
            <img src={p.avatar} alt="" className="h-24 w-24 rounded-2xl object-cover border border-border" />
            <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} className="absolute -bottom-2 -right-2 h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow"><Camera className="h-4 w-4" /></button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAvatar(e.target.files[0])} />
            {p.avatar && (
              <button type="button" data-testid="avatar-adjust-framing-btn" onClick={() => setFramingOpen(true)}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0F2C59] hover:text-[#A9802B]">
                <Crop className="h-3.5 w-3.5" /> Adjust framing
              </button>
            )}
            <PortraitFramingDialog open={framingOpen} onOpenChange={setFramingOpen} src={p.avatar} initial={p.avatar_focal}
              onSaved={(f) => setP((cur) => ({ ...cur, avatar_focal: f }))} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              {p.name}{p.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-6 w-6 text-accent" />}
            </h1>
            <p className="text-slate-500">{p.role_label} · {p.agency || "Independent"}</p>
            <span data-testid="verification-badge" className={`inline-flex items-center gap-1.5 mt-2 text-xs font-semibold px-2.5 py-1 rounded-full ${VERIF_STYLES[p.verification_status]}`}>
              <ShieldCheck className="h-3.5 w-3.5" /> {label(p.verification_status)}
            </span>
          </div>
          {!edit ? (
            <Button data-testid="edit-profile-btn" onClick={() => setEdit(true)} variant="outline" className="rounded-xl">Edit profile</Button>
          ) : (
            <div className="flex gap-2">
              <Button data-testid="save-profile-btn" onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl">Save</Button>
              <Button onClick={() => { setEdit(false); setF(p); }} variant="ghost" className="rounded-xl">Cancel</Button>
            </div>
          )}
        </div>
      </Fade>

      <PublicProfileManager account={p} />

      <div className="grid lg:grid-cols-2 gap-6">
        <Fade delay={0.05}>
          <section className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold mb-4">Professional Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>First name</Label><Input disabled={!edit} value={f.first_name || ""} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Last name</Label><Input disabled={!edit} value={f.last_name || ""} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Brokerage</Label><Input disabled={!edit} value={f.agency || ""} onChange={(e) => setF({ ...f, agency: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Primary city</Label><Input disabled={!edit} value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1.5" /></div>
              <div className="col-span-2"><Label>Short bio</Label><Textarea disabled={!edit} value={f.bio || ""} onChange={(e) => setF({ ...f, bio: e.target.value })} className="mt-1.5" /></div>
            </div>
          </section>
        </Fade>

        <Fade delay={0.08}>
          <section className="bg-card border border-border rounded-xl p-6" data-testid="org-service-areas-section">
            <h2 className="font-display text-lg font-semibold mb-4">Professional Identity &amp; Affiliation</h2>
            <div className="space-y-4">
              <div>
                <Label>Sales / Business Position <span className="text-slate-400 font-normal">(optional)</span></Label>
                {edit ? (
                  <>
                    <Select value={f.sales_position || SALES_POS_NONE} onValueChange={(v) => setF({ ...f, sales_position: v === SALES_POS_NONE ? "" : v })}>
                      <SelectTrigger data-testid="sales-position-select" className="mt-1.5"><SelectValue placeholder="Select your position" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value={SALES_POS_NONE}>Not specified</SelectItem>
                        {SALES_POSITION_GROUPS.map((g) => (
                          <div key={g.group}>
                            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.group}</div>
                            {g.items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    {f.sales_position === "OTHER" && (
                      <Input data-testid="sales-position-other-input" value={f.sales_position_other || ""} maxLength={80} onChange={(e) => setF({ ...f, sales_position_other: e.target.value })} placeholder="Enter your position" className="mt-2" />
                    )}
                  </>
                ) : (
                  <p data-testid="sales-position-display" className="mt-1.5 text-sm font-medium text-slate-800">{p.sales_position_label || "—"}</p>
                )}
                <p className="mt-1 text-[11px] text-slate-400">Your commercial/organizational position — separate from your verified professional role.</p>
              </div>

              <div>
                <Label>Affiliation status</Label>
                {edit ? (
                  <Select value={f.affiliation_status || SALES_POS_NONE} onValueChange={(v) => setF({ ...f, affiliation_status: v === SALES_POS_NONE ? "" : v })}>
                    <SelectTrigger data-testid="affiliation-status-select" className="mt-1.5"><SelectValue placeholder="Select affiliation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SALES_POS_NONE}>Not specified</SelectItem>
                      <SelectItem value="AFFILIATED">Affiliated</SelectItem>
                      <SelectItem value="INDEPENDENT">Independent / Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p data-testid="affiliation-status-display" className="mt-1.5 text-sm font-medium text-slate-800">{p.affiliation_status_label || "—"}</p>
                )}
              </div>

              {(edit ? f.affiliation_status !== "INDEPENDENT" : p.affiliation_status !== "INDEPENDENT") ? (
                <>
                  <div>
                    <Label>Organization type</Label>
                    {edit ? (
                      <Select value={f.organization_type && f.organization_type !== "INDEPENDENT" ? f.organization_type : ORG_NONE} onValueChange={(v) => setF({ ...f, organization_type: v === ORG_NONE ? "" : v })}>
                        <SelectTrigger data-testid="org-type-select" className="mt-1.5"><SelectValue placeholder="Select organization type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ORG_NONE}>Not specified</SelectItem>
                          {AFFILIATED_ORG_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p data-testid="org-type-display" className="mt-1.5 text-sm font-medium text-slate-800">{p.organization_type_label || "—"}</p>
                    )}
                  </div>
                  <div>
                    <Label>Organization name</Label>
                    {edit ? (
                      <Input data-testid="org-name-input" value={f.organization_name || ""} maxLength={120} onChange={(e) => setF({ ...f, organization_name: e.target.value })} placeholder="e.g. DMCI Homes, ABC Realty" className="mt-1.5" />
                    ) : (
                      <p data-testid="org-name-display" className="mt-1.5 text-sm font-medium text-slate-800">{p.organization_name || "—"}</p>
                    )}
                  </div>
                  <div>
                    <Label>Branch / Division / Team <span className="text-slate-400 font-normal">(optional)</span></Label>
                    {edit ? (
                      <Input data-testid="org-branch-input" value={f.organization_branch || ""} maxLength={80} onChange={(e) => setF({ ...f, organization_branch: e.target.value })} placeholder="e.g. Makati Branch, Residential Sales Division" className="mt-1.5" />
                    ) : (
                      <p data-testid="org-branch-display" className="mt-1.5 text-sm font-medium text-slate-800">{p.organization_branch || "—"}</p>
                    )}
                  </div>
                </>
              ) : (
                <p data-testid="independent-note" className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">You're set as <span className="font-medium text-slate-700">Independent / Freelance</span> — no organization needed.</p>
              )}

              <div>
                <Label>Service areas</Label>
                {edit ? (
                  <ServiceAreaPicker value={f.service_area_ids || []} onChange={(v) => setF({ ...f, service_area_ids: v })} testid="profile-service-area-picker" />
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-2" data-testid="service-areas-display">
                    {(p.service_area_ids || []).length > 0
                      ? p.service_area_ids.map((a) => <span key={a.code} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm font-medium text-slate-700">{a.name}</span>)
                      : <span className="text-sm text-slate-500">—</span>}
                  </div>
                )}
              </div>
            </div>
          </section>
        </Fade>

        <Fade delay={0.1}>
          <section className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold mb-4">Account</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{p.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Mobile</span>{edit ? <Input value={f.mobile || ""} onChange={(e) => setF({ ...f, mobile: e.target.value })} className="h-8 w-48" /> : <span className="font-medium">{p.mobile || "—"}</span>}</div>
            </div>
            <div className="mt-5 pt-5 border-t border-border">
              <h3 className="font-medium text-sm flex items-center gap-1.5 mb-3"><Lock className="h-4 w-4" /> Change password</h3>
              <div className="grid grid-cols-1 gap-2">
                <Input data-testid="current-pw" type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
                <Input data-testid="new-pw" type="password" placeholder="New password (min 8 chars)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
                <Button data-testid="change-pw-btn" onClick={changePw} variant="outline" className="rounded-xl">Update password</Button>
              </div>
            </div>
          </section>
        </Fade>

        <Fade delay={0.15}>
          <section className="bg-card border border-border rounded-xl p-6 lg:col-span-2">
            <h2 className="font-display text-lg font-semibold mb-1">Professional Credentials</h2>
            <p className="text-sm text-slate-500 mb-4">Verification is reviewed by a PropertyConnect administrator. Documents are kept private.</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold mb-4" data-testid="verification-progress">
              <span className="inline-flex items-center gap-1 text-success"><BadgeCheck className="h-3.5 w-3.5" /> Account created</span>
              <span className="text-slate-300">→</span>
              <span className={`inline-flex items-center gap-1 ${p.email_verified !== false ? "text-success" : "text-slate-400"}`}><BadgeCheck className="h-3.5 w-3.5" /> Email verified</span>
              <span className="text-slate-300">→</span>
              <span className={`inline-flex items-center gap-1 ${p.verification_status === "PROFESSIONAL_VERIFIED" ? "text-success" : p.verification_status === "PROFESSIONAL_VERIFICATION_PENDING" ? "text-info" : "text-attention"}`}>
                <ShieldCheck className="h-3.5 w-3.5" /> Professional verification{p.verification_status === "PROFESSIONAL_VERIFIED" ? "" : p.verification_status === "PROFESSIONAL_VERIFICATION_PENDING" ? " (under review)" : " (action required)"}
              </span>
            </div>
            {p.verification_status === "PROFESSIONAL_VERIFIED" ? (
              <div className="flex items-center gap-2 text-success bg-success-soft rounded-lg px-4 py-3"><BadgeCheck className="h-5 w-5" /> Your professional credentials are verified.</div>
            ) : p.verification_status === "PROFESSIONAL_VERIFICATION_PENDING" ? (
              <div className="flex items-center gap-2 text-info bg-info-soft rounded-lg px-4 py-3"><ShieldCheck className="h-5 w-5" /> Your verification is under review.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {p.verification?.reason && (
                  <div data-testid="profile-verification-reason" className="sm:col-span-2 text-sm text-critical bg-critical-soft border border-critical/30 rounded-lg px-4 py-3">
                    <span className="font-semibold">Needs correction:</span> {p.verification.reason}
                  </div>
                )}
                <Input data-testid="ver-prc" placeholder="PRC Number" value={ver.prc_number} onChange={(e) => setVer({ ...ver, prc_number: e.target.value })} />
                <Input data-testid="ver-role" placeholder="Professional role (Broker/Agent)" value={ver.role} onChange={(e) => setVer({ ...ver, role: e.target.value })} />
                <Input data-testid="ver-affiliation" placeholder="Broker affiliation (agents)" value={ver.broker_affiliation} onChange={(e) => setVer({ ...ver, broker_affiliation: e.target.value })} />
                <Input data-testid="ver-doc" placeholder="Supporting document note (optional)" value={ver.document_note} onChange={(e) => setVer({ ...ver, document_note: e.target.value })} />
                <div className="sm:col-span-2"><Button data-testid="submit-verification" onClick={submitVer} className="bg-primary text-primary-foreground rounded-xl">Submit for verification</Button></div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-slate-500 mb-2">Supporting documents (PRC certificate / ID) — private, visible only to admin reviewers.</p>
              <input ref={docRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(e.target.files[0])} />
              <Button data-testid="upload-ver-doc" onClick={() => docRef.current?.click()} variant="outline" className="rounded-xl"><Upload className="h-4 w-4 mr-1.5" /> Upload document</Button>
              {(p.verification?.documents || []).length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="my-ver-docs">
                  {p.verification.documents.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm text-slate-600"><FileText className="h-4 w-4 text-slate-400" />{d.filename}</div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </Fade>
      </div>
    </PageShell>
  );
}
